import React, { useState, useEffect } from 'react';
import { X, Folder, FileText, Calendar, Plus, Check, Edit2, Trash2, Clock, AlertCircle, ChevronRight, ChevronDown, FolderPlus } from 'lucide-react';
import { Matter, Goal, Task, Document, DocumentFolder, MatterDeadline, DocumentAssociation, db } from '../../lib/db';
import { format } from 'date-fns';
import DocumentPreview from '../documents/DocumentPreview';

interface MatterDetailModalFullScreenProps {
  matter: Matter;
  goals: Goal[];
  tasks?: Task[];
  isOpen: boolean;
  onClose: () => void;
  onEdit: (matter: Matter) => void;
  userId: string;
}

interface DeadlineFormData {
  title: string;
  description: string;
  due_date: string;
  reminder_date: string;
}

export function MatterDetailModalFullScreen({
  matter,
  goals,
  tasks,
  isOpen,
  onClose,
  onEdit,
  userId,
}: MatterDetailModalFullScreenProps) {
  const [documents, setDocuments] = useState<Document[]>([]);
  const [folders, setFolders] = useState<DocumentFolder[]>([]);
  const [deadlines, setDeadlines] = useState<MatterDeadline[]>([]);
  const [documentAssociations, setDocumentAssociations] = useState<DocumentAssociation[]>([]);
  const [selectedDocument, setSelectedDocument] = useState<Document | null>(null);
  const [showAddDeadline, setShowAddDeadline] = useState(false);
  const [editingDeadline, setEditingDeadline] = useState<MatterDeadline | null>(null);
  const [deadlineForm, setDeadlineForm] = useState<DeadlineFormData>({
    title: '',
    description: '',
    due_date: '',
    reminder_date: ''
  });
  const [expandedFolders, setExpandedFolders] = useState<Set<string>>(new Set());
  const [showNewFolder, setShowNewFolder] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [draggedDocument, setDraggedDocument] = useState<Document | null>(null);
  const [dragOverFolder, setDragOverFolder] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      loadDocuments();
      loadFolders();
      loadDeadlines();
    }
  }, [isOpen]);

  const loadDocuments = async () => {
    try {
      const [allDocuments, associations] = await Promise.all([
        db.documents.findByUserId(userId),
        db.documentAssociations.findByUserId(userId)
      ]);
      
      const matterAssociations = associations.filter(a => a.matter_id === matter.id);
      setDocumentAssociations(matterAssociations);
      
      const matterDocuments = allDocuments.filter(doc => {
        return matterAssociations.some(a => a.document_id === doc.id);
      });
      
      setDocuments(matterDocuments);
    } catch (error) {
      console.error('Error loading documents:', error);
    }
  };

  const loadFolders = async () => {
    try {
      const matterFolders = await db.documentFolders.findByMatterId(matter.id, userId);
      setFolders(matterFolders);
    } catch (error) {
      console.error('Error loading folders:', error);
    }
  };

  const loadDeadlines = async () => {
    try {
      const matterDeadlines = await db.matterDeadlines.findByMatterId(matter.id, userId);
      setDeadlines(matterDeadlines);
    } catch (error) {
      console.error('Error loading deadlines:', error);
    }
  };

  const handleAddDeadline = async () => {
    try {
      await db.matterDeadlines.create({
        matter_id: matter.id,
        user_id: userId,
        title: deadlineForm.title,
        description: deadlineForm.description || undefined,
        due_date: new Date(deadlineForm.due_date),
        reminder_date: deadlineForm.reminder_date ? new Date(deadlineForm.reminder_date) : undefined,
        is_completed: false
      });
      
      setDeadlineForm({ title: '', description: '', due_date: '', reminder_date: '' });
      setShowAddDeadline(false);
      loadDeadlines();
    } catch (error) {
      console.error('Error adding deadline:', error);
    }
  };

  const handleUpdateDeadline = async () => {
    if (!editingDeadline) return;
    
    try {
      await db.matterDeadlines.update(editingDeadline.id, userId, {
        title: deadlineForm.title,
        description: deadlineForm.description || undefined,
        due_date: new Date(deadlineForm.due_date),
        reminder_date: deadlineForm.reminder_date ? new Date(deadlineForm.reminder_date) : undefined,
      });
      
      setEditingDeadline(null);
      setDeadlineForm({ title: '', description: '', due_date: '', reminder_date: '' });
      loadDeadlines();
    } catch (error) {
      console.error('Error updating deadline:', error);
    }
  };

  const handleToggleDeadline = async (deadline: MatterDeadline) => {
    try {
      await db.matterDeadlines.update(deadline.id, userId, {
        is_completed: !deadline.is_completed
      });
      loadDeadlines();
    } catch (error) {
      console.error('Error toggling deadline:', error);
    }
  };

  const handleDeleteDeadline = async (deadline: MatterDeadline) => {
    if (!window.confirm('Are you sure you want to delete this deadline?')) return;
    
    try {
      await db.matterDeadlines.delete(deadline.id, userId);
      loadDeadlines();
    } catch (error) {
      console.error('Error deleting deadline:', error);
    }
  };

  const toggleFolder = (folderId: string) => {
    const newExpanded = new Set(expandedFolders);
    if (newExpanded.has(folderId)) {
      newExpanded.delete(folderId);
    } else {
      newExpanded.add(folderId);
    }
    setExpandedFolders(newExpanded);
  };

  const handleCreateFolder = async () => {
    if (!newFolderName.trim()) return;
    
    try {
      const maxPosition = Math.max(...folders.map(f => f.position), -1);
      
      await db.documentFolders.create({
        user_id: userId,
        name: newFolderName.trim(),
        matter_id: matter.id,
        project_id: undefined,
        parent_folder_id: undefined,
        position: maxPosition + 1
      });
      
      setNewFolderName('');
      setShowNewFolder(false);
      loadFolders();
    } catch (error) {
      console.error('Error creating folder:', error);
    }
  };

  const getDocumentsInFolder = (folderId: string | null) => {
    return documents.filter(doc => {
      const association = documentAssociations.find(a => a.document_id === doc.id);
      if (folderId === null) {
        // Return documents without a folder
        return !association || !association.folder_id;
      }
      // Return documents in specific folder
      return association && association.folder_id === folderId;
    });
  };

  const handleDocumentDragStart = (e: React.DragEvent, document: Document) => {
    setDraggedDocument(document);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleFolderDragOver = (e: React.DragEvent, folderId: string) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverFolder(folderId);
  };

  const handleFolderDragLeave = () => {
    setDragOverFolder(null);
  };

  const handleFolderDrop = async (e: React.DragEvent, folderId: string) => {
    e.preventDefault();
    setDragOverFolder(null);
    
    if (!draggedDocument) return;
    
    try {
      // Get current association
      const associations = await db.documentAssociations.findByUserId(userId);
      const currentAssociation = associations.find(a => a.document_id === draggedDocument.id);
      
      if (currentAssociation) {
        // Update existing association
        await db.documentAssociations.update(draggedDocument.id, userId, {
          folder_id: folderId
        });
      } else {
        // Create new association
        await db.documentAssociations.create({
          document_id: draggedDocument.id,
          user_id: userId,
          matter_id: matter.id,
          folder_id: folderId,
          project_id: undefined,
          goal_id: undefined,
          notes: undefined
        });
      }
      
      // Reload documents to reflect changes
      await loadDocuments();
    } catch (error) {
      console.error('Error moving document to folder:', error);
    }
    
    setDraggedDocument(null);
  };

  const handleUnfiledDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    setDragOverFolder(null);
    
    if (!draggedDocument) return;
    
    try {
      // Update association to remove folder
      await db.documentAssociations.update(draggedDocument.id, userId, {
        folder_id: undefined
      });
      
      await loadDocuments();
    } catch (error) {
      console.error('Error removing document from folder:', error);
    }
    
    setDraggedDocument(null);
  };

  if (!isOpen) return null;

  const associatedGoals = goals.filter(goal => goal.matter_id === matter.id);
  const upcomingDeadlines = deadlines.filter(d => !d.is_completed);
  const completedDeadlines = deadlines.filter(d => d.is_completed);

  return (
    <div className="fixed inset-0 z-50 overflow-hidden">
      <div className="absolute inset-0 bg-gray-500 bg-opacity-75" onClick={onClose} />
      
      <div className="absolute inset-0 bg-white flex">
        {/* Left Sidebar - Documents & Folders */}
        <div className="w-80 border-r border-gray-200 flex flex-col">
          <div className="p-4 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">Documents & Files</h3>
          </div>
          
          <div className="flex-1 overflow-y-auto p-4">
            {/* Folder Creation */}
            <div className="mb-4">
              {showNewFolder ? (
                <div className="flex items-center space-x-2">
                  <input
                    type="text"
                    value={newFolderName}
                    onChange={(e) => setNewFolderName(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleCreateFolder()}
                    placeholder="Folder name"
                    className="flex-1 px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-indigo-500"
                    autoFocus
                  />
                  <button
                    onClick={handleCreateFolder}
                    className="p-1 text-green-600 hover:text-green-700"
                  >
                    <Check className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => {
                      setShowNewFolder(false);
                      setNewFolderName('');
                    }}
                    className="p-1 text-gray-400 hover:text-gray-600"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => setShowNewFolder(true)}
                  className="flex items-center space-x-2 text-sm text-gray-600 hover:text-gray-800"
                >
                  <FolderPlus className="w-4 h-4" />
                  <span>New Folder</span>
                </button>
              )}
            </div>
            
            {/* Document Tree */}
            <div className="space-y-2">
              {folders.map(folder => (
                <div key={folder.id}>
                  <div
                    onDragOver={(e) => handleFolderDragOver(e, folder.id)}
                    onDragLeave={handleFolderDragLeave}
                    onDrop={(e) => handleFolderDrop(e, folder.id)}
                    className={`rounded transition-colors ${
                      dragOverFolder === folder.id ? 'bg-blue-100 ring-2 ring-blue-400' : ''
                    }`}
                  >
                    <button
                      onClick={() => toggleFolder(folder.id)}
                      className="flex items-center space-x-2 w-full text-left hover:bg-gray-50 p-2 rounded"
                    >
                      {expandedFolders.has(folder.id) ? (
                        <ChevronDown className="w-4 h-4 text-gray-400" />
                      ) : (
                        <ChevronRight className="w-4 h-4 text-gray-400" />
                      )}
                      <Folder className="w-4 h-4 text-gray-400" />
                      <span className="text-sm font-medium text-gray-700">{folder.name}</span>
                    </button>
                  </div>
                  
                  {expandedFolders.has(folder.id) && (
                    <div className="ml-6 mt-1 space-y-1">
                      {getDocumentsInFolder(folder.id).map(doc => (
                        <button
                          key={doc.id}
                          draggable
                          onDragStart={(e) => handleDocumentDragStart(e, doc)}
                          onClick={() => setSelectedDocument(doc)}
                          className={`flex items-center space-x-2 w-full text-left hover:bg-gray-50 p-2 rounded cursor-move ${
                            selectedDocument?.id === doc.id ? 'bg-blue-50' : ''
                          }`}
                        >
                          <FileText className="w-4 h-4 text-gray-400" />
                          <span className="text-sm text-gray-600 truncate">{doc.suggested_filename || doc.filename}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              ))}
              
              {/* Unfiled Documents */}
              {getDocumentsInFolder(null).length > 0 && (
                <div
                  onDragOver={(e) => {
                    e.preventDefault();
                    e.dataTransfer.dropEffect = 'move';
                    setDragOverFolder('unfiled');
                  }}
                  onDragLeave={handleFolderDragLeave}
                  onDrop={handleUnfiledDrop}
                  className={`rounded transition-colors ${
                    dragOverFolder === 'unfiled' ? 'bg-gray-100 ring-2 ring-gray-400' : ''
                  }`}
                >
                  <div className="text-sm font-medium text-gray-500 mb-2 p-2">Unfiled Documents</div>
                  {getDocumentsInFolder(null).map(doc => (
                    <button
                      key={doc.id}
                      draggable
                      onDragStart={(e) => handleDocumentDragStart(e, doc)}
                      onClick={() => setSelectedDocument(doc)}
                      className={`flex items-center space-x-2 w-full text-left hover:bg-gray-50 p-2 rounded cursor-move ${
                        selectedDocument?.id === doc.id ? 'bg-blue-50' : ''
                      }`}
                    >
                      <FileText className="w-4 h-4 text-gray-400" />
                      <span className="text-sm text-gray-600 truncate">{doc.original_filename}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Main Content Area */}
        <div className="flex-1 flex flex-col">
          {/* Header */}
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-2xl font-bold text-gray-900">{matter.title}</h2>
                {matter.description && (
                  <p className="mt-1 text-sm text-gray-600">{matter.description}</p>
                )}
              </div>
              <div className="flex items-center space-x-3">
                <button
                  onClick={() => {
                    onEdit(matter);
                    onClose();
                  }}
                  className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                >
                  <Edit2 className="w-4 h-4 mr-2" />
                  Edit
                </button>
                <button
                  onClick={onClose}
                  className="p-2 text-gray-400 hover:text-gray-600"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>
            </div>
          </div>

          <div className="flex-1 flex">
            {/* Document Preview Area */}
            <div className="flex-1 bg-gray-50">
              {selectedDocument ? (
                <DocumentPreview document={selectedDocument} />
              ) : (
                <div className="flex items-center justify-center h-full">
                  <div className="text-center">
                    <FileText className="mx-auto h-12 w-12 text-gray-400" />
                    <p className="mt-2 text-sm text-gray-600">Select a document to preview</p>
                  </div>
                </div>
              )}
            </div>

            {/* Right Sidebar - Deadlines & Details */}
            <div className="w-96 border-l border-gray-200 flex flex-col">
              <div className="p-4 border-b border-gray-200">
                <h3 className="text-lg font-medium text-gray-900">Deadlines & Details</h3>
              </div>
              
              <div className="flex-1 overflow-y-auto p-4 space-y-6">
                {/* Deadlines Section */}
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <h4 className="text-sm font-medium text-gray-900">Upcoming Deadlines</h4>
                    <button
                      onClick={() => {
                        setShowAddDeadline(true);
                        setEditingDeadline(null);
                        setDeadlineForm({ title: '', description: '', due_date: '', reminder_date: '' });
                      }}
                      className="text-sm text-indigo-600 hover:text-indigo-500"
                    >
                      <Plus className="w-4 h-4" />
                    </button>
                  </div>
                  
                  {showAddDeadline && !editingDeadline && (
                    <div className="bg-gray-50 rounded-lg p-4 mb-4">
                      <input
                        type="text"
                        placeholder="Deadline title"
                        value={deadlineForm.title}
                        onChange={(e) => setDeadlineForm({ ...deadlineForm, title: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md mb-2"
                      />
                      <textarea
                        placeholder="Description (optional)"
                        value={deadlineForm.description}
                        onChange={(e) => setDeadlineForm({ ...deadlineForm, description: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md mb-2"
                        rows={2}
                      />
                      <div className="grid grid-cols-2 gap-2 mb-2">
                        <div>
                          <label className="block text-xs text-gray-600 mb-1">Due Date</label>
                          <input
                            type="date"
                            value={deadlineForm.due_date}
                            onChange={(e) => setDeadlineForm({ ...deadlineForm, due_date: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md"
                          />
                        </div>
                        <div>
                          <label className="block text-xs text-gray-600 mb-1">Reminder (optional)</label>
                          <input
                            type="date"
                            value={deadlineForm.reminder_date}
                            onChange={(e) => setDeadlineForm({ ...deadlineForm, reminder_date: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md"
                          />
                        </div>
                      </div>
                      <div className="flex justify-end space-x-2">
                        <button
                          onClick={() => setShowAddDeadline(false)}
                          className="px-3 py-1 text-sm text-gray-600 hover:text-gray-800"
                        >
                          Cancel
                        </button>
                        <button
                          onClick={handleAddDeadline}
                          disabled={!deadlineForm.title || !deadlineForm.due_date}
                          className="px-3 py-1 text-sm bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-50"
                        >
                          Add
                        </button>
                      </div>
                    </div>
                  )}
                  
                  <div className="space-y-2">
                    {upcomingDeadlines.map(deadline => (
                      <div key={deadline.id} className="bg-white rounded-lg border border-gray-200 p-3">
                        {editingDeadline?.id === deadline.id ? (
                          <div className="space-y-2">
                            <input
                              type="text"
                              value={deadlineForm.title}
                              onChange={(e) => setDeadlineForm({ ...deadlineForm, title: e.target.value })}
                              className="w-full px-2 py-1 border border-gray-300 rounded"
                            />
                            <textarea
                              value={deadlineForm.description}
                              onChange={(e) => setDeadlineForm({ ...deadlineForm, description: e.target.value })}
                              className="w-full px-2 py-1 border border-gray-300 rounded"
                              rows={2}
                            />
                            <div className="grid grid-cols-2 gap-2">
                              <input
                                type="date"
                                value={deadlineForm.due_date}
                                onChange={(e) => setDeadlineForm({ ...deadlineForm, due_date: e.target.value })}
                                className="px-2 py-1 border border-gray-300 rounded"
                              />
                              <input
                                type="date"
                                value={deadlineForm.reminder_date}
                                onChange={(e) => setDeadlineForm({ ...deadlineForm, reminder_date: e.target.value })}
                                className="px-2 py-1 border border-gray-300 rounded"
                              />
                            </div>
                            <div className="flex justify-end space-x-2">
                              <button
                                onClick={() => setEditingDeadline(null)}
                                className="text-sm text-gray-600 hover:text-gray-800"
                              >
                                Cancel
                              </button>
                              <button
                                onClick={handleUpdateDeadline}
                                className="text-sm text-indigo-600 hover:text-indigo-800"
                              >
                                Save
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div>
                            <div className="flex items-start justify-between">
                              <div className="flex items-start space-x-3">
                                <button
                                  onClick={() => handleToggleDeadline(deadline)}
                                  className="mt-0.5"
                                >
                                  <div className="w-4 h-4 border border-gray-300 rounded" />
                                </button>
                                <div>
                                  <p className="text-sm font-medium text-gray-900">{deadline.title}</p>
                                  {deadline.description && (
                                    <p className="text-xs text-gray-600 mt-1">{deadline.description}</p>
                                  )}
                                  <div className="flex items-center space-x-3 mt-1">
                                    <span className="flex items-center text-xs text-red-600">
                                      <Calendar className="w-3 h-3 mr-1" />
                                      {format(new Date(deadline.due_date), 'MMM d, yyyy')}
                                    </span>
                                    {deadline.reminder_date && (
                                      <span className="flex items-center text-xs text-amber-600">
                                        <AlertCircle className="w-3 h-3 mr-1" />
                                        Reminder: {format(new Date(deadline.reminder_date), 'MMM d')}
                                      </span>
                                    )}
                                  </div>
                                </div>
                              </div>
                              <div className="flex items-center space-x-1">
                                <button
                                  onClick={() => {
                                    setEditingDeadline(deadline);
                                    setDeadlineForm({
                                      title: deadline.title,
                                      description: deadline.description || '',
                                      due_date: format(new Date(deadline.due_date), 'yyyy-MM-dd'),
                                      reminder_date: deadline.reminder_date ? format(new Date(deadline.reminder_date), 'yyyy-MM-dd') : ''
                                    });
                                  }}
                                  className="p-1 text-gray-400 hover:text-gray-600"
                                >
                                  <Edit2 className="w-3 h-3" />
                                </button>
                                <button
                                  onClick={() => handleDeleteDeadline(deadline)}
                                  className="p-1 text-gray-400 hover:text-red-600"
                                >
                                  <Trash2 className="w-3 h-3" />
                                </button>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                {/* Deliverables Section */}
                <div>
                  <h4 className="text-sm font-medium text-gray-900 mb-3">Associated Deliverables</h4>
                  <div className="space-y-2">
                    {associatedGoals.map(goal => (
                      <div key={goal.id} className="bg-white rounded-lg border border-gray-200 p-3">
                        <p className="text-sm font-medium text-gray-900">{goal.title}</p>
                        {goal.deadline && (
                          <p className="text-xs text-gray-600 mt-1">
                            Due: {format(new Date(goal.deadline), 'MMM d, yyyy')}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                {/* Completed Deadlines */}
                {completedDeadlines.length > 0 && (
                  <div>
                    <h4 className="text-sm font-medium text-gray-900 mb-3">Completed Deadlines</h4>
                    <div className="space-y-2">
                      {completedDeadlines.map(deadline => (
                        <div key={deadline.id} className="bg-gray-50 rounded-lg p-3 opacity-60">
                          <div className="flex items-center space-x-3">
                            <Check className="w-4 h-4 text-green-600" />
                            <div>
                              <p className="text-sm text-gray-700 line-through">{deadline.title}</p>
                              <p className="text-xs text-gray-500">
                                Completed: {format(new Date(deadline.completed_at!), 'MMM d, yyyy')}
                              </p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}